import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { appelApi, urlApi } from './api';
import type { TravailACorriger } from './useEspaceFormateur';
import type { DemandeEnAttente } from './useModificationsSensibles';

/**
 * Correction des devoirs et quiz (TF-457, écran `instructor-grading`). Le
 * formateur lit les travaux qu'il corrige, la direction tous ; ce qui empêche
 * un geste vient de l'API (`refus*`), l'écran ne décide rien.
 */

export type { TravailACorriger } from './useEspaceFormateur';

export interface TravailCorrige {
  cle: string;
  devoirId: string;
  titre: string;
  type: TravailACorriger['type'];
  typeLibelle: string;
  contexte: string;
  apprenantId: string;
  apprenant: string;
  promotion: string;
  siteCode: string;
  note: number;
  publieeLe: string;
  correcteur: string | null;
  rectifieeLe: string | null;
  demandeEnAttente: boolean;
}

export interface ListeCorrections {
  compteurs: { devoirs: number; quiz: number; enRetard: number; brouillons: number; corriges: number };
  aCorriger: TravailACorriger[];
  corriges: TravailCorrige[];
  /** La direction lit tous les travaux, avec leur correcteur. */
  tousLesTravaux: boolean;
}

export interface DepotACorriger {
  id: string;
  numero: number;
  deposeLe: string;
  enRetard: boolean;
  fichier: { nom: string; tailleOctets: number; typeMime: string } | null;
  liens: string[];
}

export interface QuestionCorrigee {
  id: string;
  numero: number;
  enonce: string;
  options: { lettre: string; texte: string; correcte: boolean; choisie: boolean }[];
  jouee: boolean;
  correcte: boolean;
  explication: string | null;
}

export interface CorrectionLue {
  id: string;
  note: number | null;
  commentaire: string | null;
  publiee: boolean;
  publieeLe: string | null;
  publieePar: string | null;
  rectifieeLe: string | null;
  modifieeLe: string;
  modifieePar: string | null;
  depot: number | null;
}

export interface TravailDetail {
  cle: string;
  devoir: {
    id: string;
    titre: string;
    type: TravailACorriger['type'];
    typeLibelle: string;
    contexte: string;
    consigne: string | null;
    echeance: string;
    dureeMinutes: number | null;
  };
  apprenant: { id: string; nom: string; promotion: string; siteCode: string };
  correcteur: string | null;
  statut: 'A_RENDRE' | 'RENDU' | 'CORRIGE';
  renduLe: string | null;
  enRetard: boolean;
  /** Du plus récent au plus ancien : le premier est celui que la publication corrige. */
  depots: DepotACorriger[];
  quiz: {
    bonnes: number;
    questions: number;
    noteAutomatique: number;
    termineeLe: string | null;
    tempsEcoule: boolean;
    detail: QuestionCorrigee[];
  } | null;
  correction: CorrectionLue | null;
  refusBrouillon: string | null;
  refusPublication: string | null;
  refusDemande: string | null;
  demandeEnAttente: DemandeEnAttente | null;
  validationDirecte: boolean;
  limites: { noteSur: number; commentaireMax: number };
}

export interface ResultatCorrection {
  travail: TravailDetail;
  message: string;
}

const CLE = ['corrections'] as const;

export function useCorrections() {
  return useQuery({ queryKey: [...CLE, 'liste'], queryFn: () => appelApi<ListeCorrections>('/corrections') });
}

/** `cle` = `devoirId:apprenantId`, celle des lignes de la file. */
export function useTravailACorriger(cle: string | null) {
  const [devoirId, apprenantId] = (cle ?? '').split(':');
  return useQuery({
    queryKey: [...CLE, 'travail', cle],
    queryFn: () =>
      appelApi<TravailDetail>(
        `/corrections/travaux/${encodeURIComponent(devoirId ?? '')}/${encodeURIComponent(apprenantId ?? '')}`,
      ),
    enabled: Boolean(devoirId && apprenantId),
  });
}

function cheminTravail(cle: string): string {
  const [devoirId, apprenantId] = cle.split(':');
  return `/corrections/travaux/${encodeURIComponent(devoirId ?? '')}/${encodeURIComponent(apprenantId ?? '')}`;
}

/** Après un geste : la file, le travail, le tableau de bord qui en montre la tête. */
function useApresGeste() {
  const client = useQueryClient();
  return (resultat: ResultatCorrection) => {
    client.setQueryData([...CLE, 'travail', resultat.travail.cle], resultat.travail);
    void client.invalidateQueries({ queryKey: [...CLE, 'liste'] });
    void client.invalidateQueries({ queryKey: ['tableau-de-bord-formateur'] });
  };
}

export function useEnregistrerBrouillon() {
  const apres = useApresGeste();
  return useMutation({
    mutationFn: ({ cle, note, commentaire }: { cle: string; note: number | null; commentaire: string | null }) =>
      appelApi<ResultatCorrection>(cheminTravail(cle), { methode: 'PUT', corps: { note, commentaire } }),
    onSuccess: apres,
  });
}

export function usePublierCorrection() {
  const apres = useApresGeste();
  return useMutation({
    mutationFn: ({
      cle,
      note,
      commentaire,
      depotId,
    }: {
      cle: string;
      note: number;
      commentaire: string | null;
      depotId: string | null;
    }) =>
      appelApi<ResultatCorrection>(`${cheminTravail(cle)}/publication`, {
        methode: 'POST',
        corps: { note, commentaire, depotId },
      }),
    onSuccess: apres,
  });
}

/** Le fichier d'un dépôt, servi par une route gardée et tracée. */
export function urlFichierDepotACorriger(depotId: string): string {
  return urlApi(`/corrections/depots/${encodeURIComponent(depotId)}/fichier`);
}

/** « 14,5 » ; la saisie accepte la virgule. */
export function noteLisible(note: number): string {
  return String(note).replace('.', ',');
}

/** La note saisie, au dixième, ou `null` si elle n'en est pas une. */
export function lireNote(saisie: string): number | null {
  const texte = saisie.trim().replace(',', '.');
  if (texte === '' || !/^\d{1,2}(\.\d)?$/.test(texte)) return null;
  const note = Number(texte);
  return note >= 0 && note <= 20 ? note : null;
}
